import React, { useState } from 'react';
import { Calculator, DollarSign, Clock, ShieldCheck, Sparkles, TrendingUp, Users, Building, ArrowRight } from 'lucide-react';

interface RoiCalculatorProps {
  onGetStarted: () => void;
}

export const RoiCalculator: React.FC<RoiCalculatorProps> = ({ onGetStarted }) => {
  const [locations, setLocations] = useState<number>(1);
  const [staffCount, setStaffCount] = useState<number>(18);
  const [managerHourlyRate, setManagerHourlyRate] = useState<number>(28);

  // Math models based on industry averages (Toast / TipHaus / RestaurantOps benchmarking):
  // - Average manual tip calculation time: ~45 minutes per location per shift (14 shifts/week = ~10.5 hrs/week/location)
  // - With TipValue automated sync: reduced to 15 minutes/week (<95% time savings)
  // - Average dispute resolution time: 2.5 hrs/month/location
  const weeklyHoursSavedPerLoc = 9.5; // ~38 hrs/month
  const totalMonthlyHoursSaved = Math.round(locations * weeklyHoursSavedPerLoc * 4.33);
  const totalAnnualHoursSaved = Math.round(totalMonthlyHoursSaved * 12);
  
  // Cost savings ($)
  const annualManagerLaborSavings = Math.round(totalAnnualHoursSaved * managerHourlyRate);
  // Dispute & error liability savings (~$45 per staff member annually in resolved calculation friction and retained talent)
  const disputeRiskSavings = Math.round(locations * staffCount * 65);
  const totalAnnualSavings = annualManagerLaborSavings + disputeRiskSavings;

  return (
    <section id="roi-calculator" className="py-24 bg-gradient-to-b from-gray-900 to-gray-950 text-white relative overflow-hidden scroll-mt-24">
      {/* Background glow */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[500px] bg-blue-600/10 rounded-full blur-[140px] pointer-events-none" />

      <div className="max-w-7xl mx-auto px-6 relative z-10">
        
        {/* Header */}
        <div className="text-center space-y-4 mb-16">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 bg-blue-500/10 border border-blue-500/20 rounded-full text-[10px] font-black uppercase tracking-widest text-blue-400">
            <Calculator size={14} className="text-blue-400" />
            Interactive ROI & Time-Savings Calculator
          </div>
          <h2 className="text-3xl md:text-5xl font-black uppercase tracking-tighter italic">
            Calculate Your Venue's <span className="text-blue-500">Savings</span>
          </h2>
          <p className="text-gray-400 font-medium max-w-2xl mx-auto text-sm md:text-base">
            See exactly how many manager hours and payroll dollars TipValue AI eliminates for your hospitality team each month.
          </p>
        </div>

        {/* Calculator Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
          
          {/* Controls (Left 6 cols) */}
          <div className="lg:col-span-6 bg-white/5 border border-white/10 rounded-[2.5rem] p-6 md:p-10 space-y-8 backdrop-blur-sm">
            
            {/* Slider 1: Locations */}
            <div className="space-y-3">
              <div className="flex justify-between items-center">
                <label htmlFor="roi-locations-slider" className="text-xs font-black uppercase tracking-wider text-gray-300 flex items-center gap-2">
                  <Building size={16} className="text-blue-400" />
                  Number of Locations
                </label>
                <span className="text-xl font-black text-blue-400 font-mono bg-blue-500/10 px-3 py-1 rounded-xl border border-blue-500/20">
                  {locations} {locations === 1 ? 'Location' : 'Locations'}
                </span>
              </div>
              <input 
                id="roi-locations-slider"
                type="range" 
                min="1" 
                max="15" 
                step="1"
                value={locations}
                onChange={(e) => setLocations(parseInt(e.target.value))}
                className="w-full h-2 bg-gray-700 rounded-lg appearance-none cursor-pointer accent-blue-500"
              />
              <div className="flex justify-between text-[10px] text-gray-500 font-mono">
                <span>1 Location</span>
                <span>5 Locations</span>
                <span>10 Locations</span>
                <span>15+ Locations</span>
              </div>
            </div>

            {/* Slider 2: Staff Count */}
            <div className="space-y-3">
              <div className="flex justify-between items-center">
                <label htmlFor="roi-staff-slider" className="text-xs font-black uppercase tracking-wider text-gray-300 flex items-center gap-2">
                  <Users size={16} className="text-emerald-400" />
                  Staff Members per Location
                </label>
                <span className="text-xl font-black text-emerald-400 font-mono bg-emerald-500/10 px-3 py-1 rounded-xl border border-emerald-500/20">
                  {staffCount} Staff
                </span>
              </div>
              <input 
                id="roi-staff-slider"
                type="range" 
                min="5" 
                max="100" 
                step="1"
                value={staffCount}
                onChange={(e) => setStaffCount(parseInt(e.target.value))}
                className="w-full h-2 bg-gray-700 rounded-lg appearance-none cursor-pointer accent-emerald-500"
              />
              <div className="flex justify-between text-[10px] text-gray-500 font-mono">
                <span>5 Staff</span>
                <span>25 Staff</span>
                <span>50 Staff</span>
                <span>100+ Staff</span>
              </div>
            </div>

            {/* Slider 3: Manager Hourly Cost */}
            <div className="space-y-3">
              <div className="flex justify-between items-center">
                <label htmlFor="roi-manager-rate-slider" className="text-xs font-black uppercase tracking-wider text-gray-300 flex items-center gap-2">
                  <DollarSign size={16} className="text-purple-400" />
                  Manager / Ops Hourly Rate
                </label>
                <span className="text-xl font-black text-purple-400 font-mono bg-purple-500/10 px-3 py-1 rounded-xl border border-purple-500/20">
                  ${managerHourlyRate}/hr
                </span>
              </div>
              <input 
                id="roi-manager-rate-slider"
                type="range" 
                min="18" 
                max="65" 
                step="1"
                value={managerHourlyRate}
                onChange={(e) => setManagerHourlyRate(parseInt(e.target.value))}
                className="w-full h-2 bg-gray-700 rounded-lg appearance-none cursor-pointer accent-purple-500"
              />
              <div className="flex justify-between text-[10px] text-gray-500 font-mono">
                <span>$18/hr</span>
                <span>$30/hr</span>
                <span>$45/hr</span>
                <span>$65/hr</span>
              </div>
            </div>

          </div>

          {/* Results Output (Right 6 cols) */}
          <div className="lg:col-span-6 space-y-6">
            
            {/* Total Annual Savings Card */}
            <div className="bg-gradient-to-br from-blue-600 to-indigo-700 rounded-[2.5rem] p-8 md:p-10 shadow-2xl space-y-4 border border-blue-400/30 relative overflow-hidden">
              <div className="absolute -right-6 -bottom-6 w-40 h-40 bg-white/10 rounded-full blur-2xl pointer-events-none" />
              
              <div className="flex justify-between items-start">
                <span className="text-xs font-black uppercase tracking-widest text-blue-100">
                  Estimated Total Annual Benefit
                </span>
                <span className="bg-white/20 text-white text-[10px] font-black uppercase tracking-widest px-3 py-1 rounded-full">
                  Net Positive ROI
                </span>
              </div>

              <div className="text-5xl md:text-6xl font-black tracking-tight text-white font-mono">
                ${totalAnnualSavings.toLocaleString()}
                <span className="text-sm font-normal text-blue-200 block mt-1 tracking-normal font-sans">
                  per year in combined manager labor and dispute risk reduction
                </span>
              </div>
            </div>

            {/* Metrics Breakdown Grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              
              {/* Metric 1 */}
              <div className="bg-white/5 border border-white/10 rounded-2xl p-6 space-y-2">
                <div className="flex items-center gap-2 text-blue-400">
                  <Clock size={18} />
                  <span className="text-[10px] font-black uppercase tracking-wider">Manager Time Saved</span>
                </div>
                <div className="text-2xl md:text-3xl font-black text-white font-mono">
                  {totalMonthlyHoursSaved} hrs <span className="text-xs font-normal text-gray-400">/month</span>
                </div>
                <p className="text-[11px] text-gray-400 leading-tight">
                  Eliminates {totalAnnualHoursSaved.toLocaleString()} hours of manual spreadsheet math and shift cross-verification annually.
                </p>
              </div>

              {/* Metric 2 */}
              <div className="bg-white/5 border border-white/10 rounded-2xl p-6 space-y-2">
                <div className="flex items-center gap-2 text-emerald-400">
                  <ShieldCheck size={18} />
                  <span className="text-[10px] font-black uppercase tracking-wider">Dispute Reduction</span>
                </div>
                <div className="text-2xl md:text-3xl font-black text-white font-mono">
                  &gt; 99% <span className="text-xs font-normal text-gray-400">Reduction</span>
                </div>
                <p className="text-[11px] text-gray-400 leading-tight">
                  Transparent employee shift sign-offs and auditable point splits prevent group-chat payout disputes.
                </p>
              </div>

            </div>

            {/* CTA */}
            <div className="pt-2">
              <button
                id="btn-roi-get-started"
                onClick={onGetStarted}
                className="w-full py-5 bg-white text-gray-950 hover:bg-blue-500 hover:text-white rounded-2xl font-black uppercase tracking-widest text-sm transition-all shadow-xl active:scale-[0.99] flex items-center justify-center gap-2"
              >
                <span>Automate Your Venues Now</span>
                <ArrowRight size={16} />
              </button>
            </div>

          </div>

        </div>

      </div>
    </section>
  );
};
